import React from 'react';
import Store from 'store';
import { getIconPath } from 'common';
import { formatNumber, replaceReact } from 'utils/format';

class DataClass {
  options = {};

  getItemIconId(id, charData) {
    if (typeof id !== "string") {
      id = this.gemColors[id[1]].id + (id[0] + 1).toString().padStart(2, "0");
    }
    if (this.legendaryGems[id]) {
      id = this.legendaryGems[id].id;
    }
    const icon = this.itemIcons[this.itemById[id]?.realid || id];
    if (icon && icon[1]) {
      if (typeof icon[1] === "number") {
        return icon[1];
      } else {
        const icons = icon[1];
        const idxClass = charData ? this.classToIcon[charData.class] : 0;
        const idxGender = (charData && charData.gender === "male" ? 0 : 1);
        if (icons[idxClass + idxGender] !== undefined) return icons[idxClass + idxGender];
        else if (icons[idxClass] !== undefined) return icons[idxClass];
        else if (icons[idxGender] !== undefined) return icons[idxGender];
        else return icons[0];
      }
    }
    const item = this.itemById[id];
    if (item && item.icon) {
      return item.icon;
    }
    return 0;
  }

  getItemIcon(...args) {
    return getIconPath(this.getItemIconId(...args));
  }

  getStatCount(id) {
    const item = this.itemById[id];
    let primary = 4, secondary = 2;
    if (item.primary !== undefined) {
      primary = item.primary;
    } else if (["quiver", "source", "mojo", "phylactery"].indexOf(item.type) >= 0) {
      primary = 5;
    } else if (!this.itemTypes[item.type] || item.type === 'primeshard' || item.type === 'lessershard') {
      primary = 0;
    }
    if (item.secondary !== undefined) {
      secondary = item.secondary;
    } else if (!this.itemTypes[item.type] || item.type === 'primeshard' || item.type === 'lessershard') {
      secondary = 0;
    }
    return [primary, secondary];
  }

  extendStats(dst, name) {
    if (typeof name != "string") {
      for (const item of name) {
        this.extendStats(dst, item);
      }
    } else if (this.statGroups[name]) {
      for (const group of this.statGroups[name]) {
        this.extendStats(dst, group);
      }
    } else if (this.stats[name]) {
      dst.push(name);
    }
    return dst;
  }
    
  mergeStats(dst, limits, src) {
    if (src) {
      for (const stat in src) {
        let limit = src[stat];
        if (typeof limit == "string") {
          limit = limits[limit];
        }
        for (let name of this.extendStats([], stat)) {
          dst[name] = limit;
        }
      }
    }
    return dst;
  }

  mergeStatsSub(dst, limits, src, required) {
    if (!required) {
      return this.mergeStats(dst, limits, src.affixes);
    } else if (required === "only") {
      return this.mergeStats(dst, limits, src.required);
    } else {
      this.mergeStats(dst, limits, src.affixes);
      return this.mergeStats(dst, limits, src.required);
    }
  }

  getLimits(id, ancient) {
    if (id && this.itemById[id]) {
      if (this.qualities[this.itemById[id].quality].ancient && ancient) {
        return this.statLimits.ancient;
      } else if (this.itemById[id].quality === "rare") {
        return this.statLimits.rare;
      } else {
        return this.statLimits.legendary;
      }
    } else {
      return this.statLimits.rare;
    }
  }

  smartListStats(src, affixes, charClass) {
    const list = this.extendStats([], src);
    if (list.length === 0) {
      return list;
    }
    const output = [];
    for (let stat of list) {
      if (!this.stats[stat] || !charClass || !this.stats[stat].classes || this.stats[stat].classes.indexOf(charClass) >= 0) {
        if (!affixes || affixes[stat]) {
          output.push(stat);
        }
      }
    }
    if (output.length === 0) {
      const first = list.find(stat => !affixes || affixes[stat]);
      if (first) {
        output.push(first);
      }
    }
    return output;
  }

  smartExtend(dst, src, blocked) {
    if (src) {
      for (const stat of src) {
        let group = stat;
        while (this.statGroups[group]) {
          group = this.statGroups[group][0];
        }
        group = ((this.stats[group] && this.stats[group].group) || group);
        if (!blocked[group]) {
          dst[stat] = true;
          blocked[group] = true;
        }
      }
    }
  }

  getItemPreset(id) {
    const item = this.itemById[id];
    if (!item) return [];
    const type = item.type;
    const preset = {};
    const blocked = {};
    this.smartExtend(preset, item.preset, blocked);
    if (this.itemTypes[type] && item.quality !== 'ethereal') {
      this.smartExtend(preset, this.itemTypes[type].preset, blocked);
      const itemSlot = this.itemTypes[type].slot;
      if (this.metaSlots[itemSlot]) {
        this.smartExtend(preset, this.metaSlots[itemSlot].preset, blocked);
      } else if (itemSlot) {
        this.smartExtend(preset, this.itemSlots[itemSlot].preset, blocked);
      }
    }
    return Object.keys(preset);
  }

  getItemAffixesById_(id, ancient, required) {
    const item = this.itemById[id];
    if (!item) return {};
    const type = item.type;
    const limits = this.getLimits(id, ancient);
    const stats = {};
    if (this.itemTypes[type] && item.quality !== 'ethereal') {
      const itemSlot = this.itemTypes[type].slot;
      if (this.metaSlots[itemSlot]) {
        this.mergeStatsSub(stats, limits, this.metaSlots[itemSlot], required);
      } else if (itemSlot) {
        this.mergeStatsSub(stats, limits, this.itemSlots[itemSlot], required);
      }
      this.mergeStatsSub(stats, limits, this.itemTypes[type], required);
    }
    this.mergeStatsSub(stats, limits, item, required);
    if (ancient === "primal" || ancient === "sanctified") {
      for (const stat in stats) {
        //if (this.stats[stat] && this.stats[stat].base) continue;
        if ((stats[stat].max && stats[stat].min < stats[stat].max) || (stats[stat].max2 && stats[stat].min2 < stats[stat].max2)) {
          stats[stat] = Object.assign({}, stats[stat]);
          if (stats[stat].best === "min") {
            if (stats[stat].max) stats[stat].max = stats[stat].min;
            if (stats[stat].max2) stats[stat].max2 = stats[stat].min2;
          } else {
            if (stats[stat].max) stats[stat].min = stats[stat].max;
            if (stats[stat].max2) stats[stat].min2 = stats[stat].max2;
          }
        }
      }
    }
    if ((ancient || item.quality === 'ethereal') && required !== "only") {
      this.mergeStats(stats, limits, {
        caldesanns: {step: 5, min: 5, max: 750},
      });
    }
    if (ancient === 'sanctified' && required !== "only") {
      this.mergeStats(stats, limits, {
        sanctified: {},
      });
    }
    if (item.quality === 'ethereal' && required !== "only") {
      this.mergeStats(stats, limits, {
        sockets: {min: 1, max: 1},
      });
    }
    return stats;
  }

  getItemAffixesById(itemId, ancient, required) {
    if (!required || required === "only") return this.getItemAffixesById_(itemId, ancient, required);
    const afx = this.getItemAffixesById_(itemId, ancient, false);
    const req = this.getItemAffixesById_(itemId, ancient, "only");
    const dst = {};
    for (const id in req) {
      if ((id in afx) && req[id].noblock) {
        const _add = (x, y) => {
          if (this.stats[id] && this.stats[id].dr) {
            return 100 - (100 - x) * (100 - y) / 100;
          } else {
            return x + y;
          }
        };
        dst[id] = Object.assign({}, req[id]);
        if (afx[id].max) dst[id].max = _add(dst[id].max, afx[id].max);
        if (afx[id].max2) dst[id].max2 = _add(dst[id].max2, afx[id].max2);
      } else {
        dst[id] = req[id];
      }
    }
    for (const id in afx) {
      if (!(id in req)) dst[id] = afx[id];
    }
    return dst;
  }

  getOffhandTypes(charData, mhtype, follower) {
    const charClass = follower ? charData.follower : charData.class;
    const noDual = !this.classes[charClass].dualwield;
    let twohand = false;
    if (charClass !== "crusader" && (mhtype || (charData.items.mainhand && this.itemById[charData.items.mainhand.id]))) {
      const maintype = (mhtype || this.itemById[charData.items.mainhand.id].type);
      if (this.itemTypes[maintype] && this.itemTypes[maintype].slot === "twohand") {
        twohand = true;
      }
    }
    const result = {};
    let wpn = false;
    for (let [type, typeData] of Object.entries(this.itemSlots.offhand.types)) {
      if (twohand && type !== "quiver") continue;
      if (noDual && typeData.slot === "onehand" && type !== "handcrossbow" && type !== "repeatingcrossbow") continue;
      if (typeData.classes && typeData.classes.indexOf(charClass) < 0) {
        continue;
      }
      result[type] = typeData;
      if (type !== "customwpn" && typeData.weapon) wpn = true;
    }
    if (!wpn) delete result.customwpn;
    return result;
  }

  formatBonus(charClass, format, values, spans, stat) {
    const _L = this._bnet_locale;

    if (!(values instanceof Array)) {
      const tmp = values;
      values = [];
      if (tmp.min && tmp.max) {
        values.push({min: tmp.min, max: tmp.max});
      }
      if (tmp.min2 && tmp.max2) {
        values.push({min: tmp.min2, max: tmp.max2});
      }
    }

    if (values.length <= 1) {
      format = format.replace(/%d-%d/g, "%d");
    }

    const hasSpan = format.includes("%d-%d");

    if (spans) {
      format = format.replace(/(\d|%(?:\{\d+\})?(?:d|\.\df))-(\d|%(?:\{\d+\})?(?:d|\.\df))/g, "$1\u2013$2");
    }

    let index = 0;
    function fmtValue(val, p) {
      if (typeof val === "number") {
        return formatNumber(val, p, 10000);
      } else if (val && val.min && val.max === val.min) {
        return formatNumber(val.min, p, 10000);
      } else if (val && val.min && val.max) {
        var res = formatNumber(val.min || 0, p, 10000) + (spans ? "\u2013" : "-") + formatNumber(val.max || 0, p, 10000);
        if (hasSpan) {
          res = "(" + res + ")";
        }
        return res;
      } else {
        return "0";
      }
    }

    // option 1: substitution; color if (d|f) but not (p|%)
    // \+? % \+? ARG? (d|p|\.\df|%) (%%)?
    // option 2: number+percent coloring
    // NUM(-NUM)?%%
    // option 3: newline bullet
    // \n\*|\r\n

    format = replaceReact(format, /(\+?)%(\+?)(?:\{(\d+)\})?(d|p|\.\df|%)(?:(%)%)?|([\d,]*\d(?:\.\d+)?(?:[-\u2013][\d,]*\d)?)%%|(\n\*|\r\n)/g,
    (all, prePlus="", postPlus="", arg, type, percent="", numbers, newline) => {
      if (type === "%") {
        return prePlus + "%";
      } else if (type) {
        if (!arg) {
          arg = index++;
        } else {
          arg = parseInt(arg);
        }
        const val = (arg >= values.length ? 0 : values[arg]);
        let text;
        if (type === "d") {
          text = prePlus + (val >= 0 ? postPlus : "") + fmtValue(val, 0) + percent;
        } else if (type === "p") {
          const passive = this.allPassives[val];
          return passive?.[1].name || "unknown";
        } else {
          text = prePlus + (val >= 0 ? postPlus : "") + fmtValue(val, parseInt(type[1])) + percent;
        }
        if (spans > 1) {
          return <span className="d3-color-blue"><span className="value">{text}</span></span>;
        } else if (spans) {
          return <span className="value">{text}</span>;
        } else {
          return text;
        }
      } else if (numbers && spans) {
        return <span className="value">{numbers}%</span>;
      } else if (newline && spans) {
        return <React.Fragment><br/><span className="tooltip-icon-bullet"/></React.Fragment>;
      } else {
        return all;
      }
    });

    if (spans) {
      let reqClass;
      if (stat && this.stats[stat] && this.stats[stat].class) {
        reqClass = this.stats[stat].class;
      } else if (stat && this.itemPowerClasses[stat]) {
        reqClass = this.itemPowerClasses[stat];
      }
      if (reqClass && charClass !== reqClass) {
        format.push(" ", <span key="classReq" className="d3-color-red">{_L`(${this.classes[reqClass].name} Only)`}</span>);
      } else if (reqClass && this.stats[stat]) {
        format.push(" " + _L`(${this.classes[reqClass].name} Only)`);
      }
      if (stat && this.stats[stat] && this.stats[stat].caldesanns) {
        format.push(" ", <span key="augment" className="d3-color-orange">{_L`(Caldesann's Despair Rank ${(values[0] / 5).toFixed(0)})`}</span>);
      }
    }
    return format;
  }

  addOption(name, value, perProfile=false) {
    this.options[name] = {
      initial: value,
      profile: perProfile,
      select: this.getOption.bind(this, name),
    };
  }
  getOption(name, state) {
    if (!state) state = Store.instance.getState();
    const option = this.options[name];
    if (!option) return null;
    if (option.profile) {
      state = state.profiles[state.activeProfile];
    }
    return state.active[name] === undefined ? option.initial : state.active[name];
  }
  setOption(name, value, dispatch) {
    if (!dispatch) dispatch = Store.dispatch;
    const option = this.options[name];
    if (!option) return;
    if (option.profile) {
      dispatch = dispatch.profile;
    }
    dispatch.setActive(name, value);
  }

  useOption(name) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return Store.useSelector(this.options[name].select);
  }
}

const Data = new DataClass();

export default Data;
